/**
 * presenceService — REST fallbacks for connection-scoped presence (Phase C).
 *
 * The live path is SignalR (`ReceivePresence` on the messaging/chat hub); this
 * REST client seeds initial state and toggles invisible mode. A non-owner never
 * receives an 'invisible' status — the server strips it at projection.
 */

import { apiService } from './apiService';
import type { ConnectionPresence } from '../../types/social';

const BASE = '/api/presence';

/** Current presence for the caller's accepted, non-blocked connections. */
export async function getConnectionsPresence(): Promise<ConnectionPresence[]> {
  const res = await apiService.get<ConnectionPresence[]>(`${BASE}/connections`);
  if (res.error) throw new Error(res.error);
  return res.data ?? [];
}

/**
 * Opt in/out of invisible mode. While invisible the server never emits the
 * caller's presence to anyone (they still receive others').
 */
export async function setInvisible(invisible: boolean): Promise<boolean> {
  const res = await apiService.post<{ success: boolean }>(`${BASE}/invisible`, { invisible });
  if (res.error) throw new Error(res.error);
  return res.data?.success ?? true;
}

/** Current invisible state for the preferences toggle. */
export async function getInvisible(): Promise<boolean> {
  const res = await apiService.get<{ invisible: boolean }>(`${BASE}/invisible`);
  if (res.error) throw new Error(res.error);
  return res.data?.invisible ?? false;
}

export const presenceService = {
  getConnectionsPresence,
  setInvisible,
  getInvisible,
};

export default presenceService;
